import React, { useEffect, useId, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../../helpers/AuthContext';
import {
  PAYBACK_COPY,
  PAYBACK_HELP_PATH,
  coverageNotes,
  createPaybackClient,
  formatComparisonFees,
  formatCycleRange,
  formatUsdCents,
} from './sellerPlanPayback';
import useSellerPlanPayback from './useSellerPlanPayback';
import './SellerPlanPayback.css';

/** The "How we calculated this" detail shared by the popup and the lasting card. */
export function MilestoneDetails({ milestone }) {
  const notes = coverageNotes(milestone.coverage);
  return (
    <details className="sppb-details">
      <summary>{PAYBACK_COPY.detailsSummary}</summary>
      <dl className="sppb-figures">
        <div>
          <dt>Billing cycle</dt>
          <dd>{formatCycleRange(milestone)}</dd>
        </div>
        <div>
          <dt>Your qualifying sales</dt>
          <dd>
            {formatUsdCents(milestone.salesCents)}
            <small>{PAYBACK_COPY.salesNote}</small>
          </dd>
        </div>
        <div>
          <dt>Illustrative fee at 25%</dt>
          <dd>
            {formatComparisonFees(milestone.salesCents)}
            <small>25% of your qualifying sales, not rounded.</small>
          </dd>
        </div>
        <div>
          <dt>Your subscription payment</dt>
          <dd>
            {formatUsdCents(milestone.subscriptionPaidCents)}
            <small>What you actually paid for this billing cycle.</small>
          </dd>
        </div>
      </dl>
      {notes.length > 0 && (
        <ul className="sppb-notes">
          {notes.map(note => <li key={note}>{note}</li>)}
        </ul>
      )}
      <p className="sppb-hypothetical">{PAYBACK_COPY.hypothetical}</p>
    </details>
  );
}

const FOCUSABLE = 'a[href], button:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

/**
 * Presentational celebration dialog. Portaled to the body so the dashboard's unscoped
 * `div.page` descendant selectors never restyle it. Escape, the close button, the backdrop and
 * "Got it" all call `onDismiss`, which is what acknowledges the milestone.
 */
export function SellerPlanPaybackDialog({ milestone, helpPath = PAYBACK_HELP_PATH, onDismiss }) {
  const titleId = useId();
  const bodyId = useId();
  const dialogRef = useRef(null);
  const pressedBackdrop = useRef(false);

  useEffect(() => {
    const previous = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.focus();
    return () => {
      document.body.style.overflow = overflow;
      if (previous && typeof previous.focus === 'function' && document.contains(previous)) {
        previous.focus();
      }
    };
  }, []);

  const onKeyDown = event => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onDismiss();
      return;
    }
    if (event.key !== 'Tab' || !dialogRef.current) return;
    const items = [...dialogRef.current.querySelectorAll(FOCUSABLE)];
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === dialogRef.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <div
      className="sppb-overlay"
      onMouseDown={event => { pressedBackdrop.current = event.target === event.currentTarget; }}
      onClick={event => {
        if (pressedBackdrop.current && event.target === event.currentTarget) onDismiss();
        pressedBackdrop.current = false;
      }}
    >
      <div
        className="sppb-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        tabIndex={-1}
        ref={dialogRef}
        onKeyDown={onKeyDown}
      >
        <button type="button" className="sppb-close" aria-label="Close" onClick={onDismiss}>
          <i className="fa-solid fa-xmark" aria-hidden="true" />
        </button>
        <div className="sppb-badge" aria-hidden="true"><i className="fa-solid fa-trophy" /></div>
        <h2 id={titleId}>{PAYBACK_COPY.headline}</h2>
        <p id={bodyId}>{PAYBACK_COPY.body}</p>
        <MilestoneDetails milestone={milestone} />
        <div className="sppb-actions">
          <button type="button" className="button" onClick={onDismiss}>Got it</button>
          <a className="sppb-help" href={helpPath} target="_blank" rel="noopener">Learn more</a>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Popup controller. Mounts nothing visible until the server grants a presentation lease for the
 * active, unacknowledged milestone. `client` and `channelId` default to the AuthContext wrapper
 * and the active channel; the bktestpage harness overrides them with scripted scenarios.
 */
export default function SellerPlanPaybackController({
  client: clientOverride,
  channelId: channelOverride,
  onDebug,
}) {
  const auth = useAuth();
  const client = useMemo(
    () => clientOverride || createPaybackClient({ fetch: auth.fetch, apiUrl: auth.API_URL }),
    [clientOverride, auth.fetch, auth.API_URL],
  );
  const channelId = channelOverride === undefined ? (auth.channel?.id ?? null) : channelOverride;
  const {
    status, popup, payback, dismiss, debug,
  } = useSellerPlanPayback({ client, channelId, autoClaim: true });
  // Harness hook only: the snapshot and state flags, never the lease token (the hook has none to give).
  useEffect(() => {
    if (onDebug) onDebug({ status, payback, popupOpen: Boolean(popup), ...debug });
  }, [onDebug, status, payback, popup, debug]);
  if (!popup) return null;
  return (
    <SellerPlanPaybackDialog
      milestone={popup}
      helpPath={payback?.helpPath}
      onDismiss={dismiss}
    />
  );
}
